/**
 * Sends Elevar navigation events for PDP interactions that update the current
 * product/variant URL without a full page load.
 *
 * Compliance note, Elevar Support 2026-05-11 / Ticket 45147083580:
 * when a PDP color/size/variant interaction navigates to a different product
 * variant URL, Elevar recommends a new `dl_view_item` and a new `dl_user_data`.
 *
 * Optional washbasin/addon selection does not navigate the PDP URL and was not
 * explicitly answered by Elevar, so those component view-item events are opt-in
 * only via `?bl_component_view_item=on`.
 */
(function () {
  'use strict';

  if (window.__blElevarViewItemBridgeInstalled) return;
  window.__blElevarViewItemBridgeInstalled = true;

  const VIEW_ITEM_EVENT = 'dl_view_item';
  const USER_DATA_EVENT = 'dl_user_data';
  const RETRY_DELAY = 120;
  // Give Elevar's app embed first chance to react to history/variant changes.
  const NATIVE_NAVIGATION_DELAY = 900;
  const VARIANT_NAVIGATION_DEBOUNCE = 160;
  const MAX_RETRIES = 30;
  const params = new URLSearchParams(window.location.search);
  const VERBOSE = (params.get('bl_debug') || '').includes('cart') || (params.get('bl_debug') || '').includes('elevar');
  const componentViewMode = (params.get('bl_component_view_item') || '').toLowerCase();
  const COMPONENT_VIEW_ITEM_ENABLED = ['on', '1', 'true', 'custom'].includes(componentViewMode);
  let lastPublishedViewItemKey = '';
  let lastPublishedViewItemAt = 0;
  let lastPublishedUserDataKey = '';
  let lastPublishedUserDataAt = 0;
  let lastHistoryNavigationIndex = null;
  let lastHistoryNavigationAt = 0;
  let pendingVariantNavigationTimer = null;

  function logVerbose(label, payload) {
    if (!VERBOSE) return;
    // eslint-disable-next-line no-console
    console.log(
      `%c[BL-view-item-bridge] ${label}`,
      'color:#2563eb;font-weight:bold',
      payload || ''
    );
  }

  function safeJsonParse(value, fallback = null) {
    if (!value) return fallback;

    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function clone(value) {
    return safeJsonParse(JSON.stringify(value), null);
  }

  function normalizeId(value) {
    return value == null ? '' : String(value).trim();
  }

  function getElevarLayer() {
    return Array.isArray(window.ElevarDataLayer) ? window.ElevarDataLayer : null;
  }

  function getLatestEvent(eventName, minIndex = 0) {
    const layer = getElevarLayer();
    if (!layer) return null;

    for (let index = layer.length - 1; index >= 0; index -= 1) {
      if (index < minIndex) break;
      if (layer[index]?.event === eventName) {
        return layer[index];
      }
    }

    return null;
  }

  function getLatestViewItemEvent(minIndex = 0) {
    return getLatestEvent(VIEW_ITEM_EVENT, minIndex);
  }

  function getLatestUserDataEvent(minIndex = 0) {
    return getLatestEvent(USER_DATA_EVENT, minIndex);
  }

  function getLayerLength() {
    const layer = getElevarLayer();
    return layer ? layer.length : 0;
  }

  function rememberHistoryNavigationBoundary() {
    lastHistoryNavigationIndex = getLayerLength();
    lastHistoryNavigationAt = Date.now();
  }

  function getNavigationStartIndex() {
    if (lastHistoryNavigationIndex != null && Date.now() - lastHistoryNavigationAt < 2000) {
      return lastHistoryNavigationIndex;
    }

    return getLayerLength();
  }

  function installHistoryBoundaryTracker() {
    if (window.__blElevarHistoryBoundaryTrackerInstalled) return;
    window.__blElevarHistoryBoundaryTrackerInstalled = true;

    ['pushState', 'replaceState'].forEach((methodName) => {
      const original = window.history?.[methodName];
      if (typeof original !== 'function') return;

      window.history[methodName] = function (...args) {
        rememberHistoryNavigationBoundary();
        return original.apply(this, args);
      };
    });
  }

  function getFirstEventItem(eventPayload) {
    const ecommerce = eventPayload?.ecommerce || {};
    const itemCollections = [
      ecommerce.detail?.products,
      ecommerce.detail?.items,
      ecommerce.items,
      ecommerce.products
    ];

    for (const collection of itemCollections) {
      if (Array.isArray(collection) && collection[0]) return collection[0];
    }

    return null;
  }

  function getEventItemIds(eventPayload) {
    const firstItem = getFirstEventItem(eventPayload) || {};

    return {
      productId: normalizeId(
        firstItem.product_id
        || firstItem.productId
        || firstItem.shopify_product_id
        || firstItem.shopifyProductId
        || eventPayload?.product_id
        || eventPayload?.productId
        || eventPayload?.shopify_product_id
        || eventPayload?.shopifyProductId
      ),
      variantId: normalizeId(
        firstItem.variant_id
        || firstItem.variantId
        || firstItem.shopify_variant_id
        || firstItem.shopifyVariantId
        || eventPayload?.variant_id
        || eventPayload?.variantId
        || eventPayload?.shopify_variant_id
        || eventPayload?.shopifyVariantId
      )
    };
  }

  function getCurrencyCode(fallback = '') {
    return (
      fallback
      || window.Shopify?.currency?.active
      || window.FoxTheme?.settings?.currencyCode
      || document.documentElement.getAttribute('data-currency')
      || ''
    );
  }

  function centsToDecimal(cents) {
    const numeric = Number.parseInt(cents || 0, 10);
    if (!Number.isFinite(numeric)) return '0.00';
    return (numeric / 100).toFixed(2);
  }

  function normalizeUrl(url, variantId = '') {
    try {
      const normalizedUrl = new URL(url || window.location.href, window.location.origin);
      if (variantId) normalizedUrl.searchParams.set('variant', variantId);
      return normalizedUrl.toString();
    } catch (error) {
      return url || window.location.href;
    }
  }

  function normalizeImageUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return `${window.location.protocol}${url}`;
    return url;
  }

  function readMainProductContext(source = 'pdp-navigation') {
    const root = document.querySelector('product-info[id^="MainProduct-"]') || document.querySelector('product-info');
    const context = safeJsonParse(root?.querySelector('[data-bl-elevar-product]')?.textContent, {});
    const selectedVariant = safeJsonParse(
      root?.querySelector('variant-selects [data-selected-variant], variant-radios [data-selected-variant]')?.textContent,
      null
    );

    const product = context.product || {};
    const variant = selectedVariant || context.variant || {};
    const variantImage = variant.featured_image?.src || variant.featured_image || variant.image;

    return {
      source,
      productId: product.id || root?.dataset.productId || '',
      variantId: variant.id || context.variant?.id || '',
      title: product.title || document.querySelector('.product__title')?.textContent?.trim() || '',
      vendor: product.vendor || '',
      type: product.type || '',
      handle: product.handle || '',
      variantTitle: variant.title || context.variant?.title || '',
      sku: variant.sku || context.variant?.sku || '',
      priceCents: variant.price || context.variant?.priceCents || 0,
      compareAtPriceCents: variant.compare_at_price || context.variant?.compareAtPriceCents || 0,
      image: normalizeImageUrl(variantImage || product.image || ''),
      url: normalizeUrl(window.location.href, variant.id || context.variant?.id || ''),
      currencyCode: getCurrencyCode(context.currencyCode)
    };
  }

  function readWashbasinContext(detail = {}) {
    const basinSelector = detail.basinId && window.CSS?.escape
      ? `[data-washbasin-selector-v2] [data-washbasin-id="${CSS.escape(String(detail.basinId))}"]`
      : '';
    const item = detail.item || (
      basinSelector ? document.querySelector(basinSelector) : null
    );
    const variantId = String(detail.variantId || '');
    const variantData = safeJsonParse(item?.dataset.variantData, {});
    const selectedVariant = variantData?.[variantId] || {};

    return {
      source: 'washbasin',
      productId: detail.productId || item?.dataset.productId || '',
      variantId,
      title: detail.productTitle || item?.dataset.productTitle || item?.dataset.washbasinName || '',
      vendor: detail.productVendor || item?.dataset.productVendor || '',
      type: detail.productType || item?.dataset.productType || '',
      handle: detail.productHandle || item?.dataset.productHandle || '',
      variantTitle: detail.variantTitle || selectedVariant.title || '',
      sku: detail.sku || selectedVariant.sku || '',
      priceCents: detail.priceCents || selectedVariant.priceCents || 0,
      compareAtPriceCents: detail.compareAtPriceCents || selectedVariant.compareAtPriceCents || 0,
      image: normalizeImageUrl(detail.image || item?.dataset.washbasinImage || ''),
      url: normalizeUrl(detail.url || item?.dataset.productUrl || window.location.href, variantId),
      currencyCode: getCurrencyCode(detail.currencyCode)
    };
  }

  function closestElement(target, selector) {
    return target instanceof Element ? target.closest(selector) : null;
  }

  function readWashbasinConfig(container) {
    return safeJsonParse(container?.querySelector('[data-washbasin-config]')?.textContent, {});
  }

  function getMainProductRoot() {
    return (
      document.querySelector('product-info[id^="MainProduct-"]')
      || document.querySelector('product-info')
    );
  }

  function getSelectedMainVariant() {
    const root = getMainProductRoot();
    const selectedVariantNode = root?.querySelector(
      'variant-selects [data-selected-variant], variant-radios [data-selected-variant]'
    );

    return safeJsonParse(selectedVariantNode?.textContent, null);
  }

  function getCurrentMainVariantId() {
    const selectedVariant = getSelectedMainVariant();
    if (selectedVariant?.id) return String(selectedVariant.id);

    const root = getMainProductRoot();
    return String(root?.querySelector('form[action*="/cart/add"] [name="id"]')?.value || '');
  }

  function normalizeWidthKey(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function extractWidthNumber(value) {
    return String(value || '').replace(/[^\d]/g, '');
  }

  function looksLikeWidthValue(value) {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return false;
    if (/^\d+([.,]\d+)?$/.test(normalized)) return true;
    if (/\d\s*(cm|mm)\b/.test(normalized)) return true;
    if (/^\d+([.,]\d+)?\s*(x|×|-|\/)\s*\d+([.,]\d+)?/.test(normalized)) return true;
    return extractWidthNumber(normalized).length >= 2 && !/[a-z]/.test(normalizeWidthKey(normalized));
  }

  function getCurrentWashbasinWidth(container) {
    const config = readWashbasinConfig(container);
    const mainVariantId = getCurrentMainVariantId();
    if (mainVariantId && config.variantWidths?.[String(mainVariantId)]) {
      return config.variantWidths[String(mainVariantId)];
    }

    const selectedVariant = getSelectedMainVariant();
    const selectedVariantWidth = Array.isArray(selectedVariant?.options)
      ? selectedVariant.options.find((optionValue) => looksLikeWidthValue(optionValue))
      : '';
    if (selectedVariantWidth) return selectedVariantWidth;

    return config.currentWidth || '';
  }

  function getWashbasinMatchForWidth(item, width) {
    const mapping = safeJsonParse(item?.dataset.mapping, {});
    const prices = safeJsonParse(item?.dataset.prices, {});
    if (!mapping || typeof mapping !== 'object') return { variantId: '', price: 0 };

    let matchedKey = width && Object.prototype.hasOwnProperty.call(mapping, width) ? width : '';
    if (!matchedKey && width) {
      const targetKey = normalizeWidthKey(width);
      const targetNumber = extractWidthNumber(width);

      matchedKey = Object.keys(mapping).find((key) => {
        const candidateKey = normalizeWidthKey(key);
        const candidateNumber = extractWidthNumber(key);
        return candidateKey === targetKey || Boolean(targetNumber && candidateNumber === targetNumber);
      }) || '';
    }

    if (!matchedKey) return { variantId: '', price: 0 };

    return {
      variantId: String(mapping[matchedKey] || ''),
      price: Number.parseInt(prices[matchedKey] || 0, 10) || 0
    };
  }

  function findWashbasinItem(container, basinId) {
    return Array.from(container?.querySelectorAll('.washbasin-selector__item[data-washbasin-id]') || [])
      .find((item) => item.dataset.washbasinId === String(basinId)) || null;
  }

  function readWashbasinDomContext(container, item) {
    if (!container || !item || item.hasAttribute('data-washbasin-none')) return null;

    const width = getCurrentWashbasinWidth(container);
    const match = getWashbasinMatchForWidth(item, width);
    if (!match.variantId) return null;

    const variantData = safeJsonParse(item.dataset.variantData, {});
    const selectedVariantData = variantData?.[String(match.variantId)] || {};
    const config = readWashbasinConfig(container);

    return readWashbasinContext({
      basinId: item.dataset.washbasinId || '',
      item,
      productId: item.dataset.productId || '',
      productHandle: item.dataset.productHandle || '',
      productTitle: item.dataset.productTitle || item.dataset.washbasinName || '',
      productVendor: item.dataset.productVendor || '',
      productType: item.dataset.productType || '',
      variantId: match.variantId,
      variantTitle: String(width || selectedVariantData.title || ''),
      sku: selectedVariantData.sku || '',
      priceCents: match.price || selectedVariantData.priceCents || 0,
      compareAtPriceCents: selectedVariantData.compareAtPriceCents || 0,
      image: item.dataset.washbasinImage || '',
      url: item.dataset.productUrl || '',
      currencyCode: config.currencyCode || ''
    });
  }

  function scheduleWashbasinDomViewItem(container, item) {
    window.setTimeout(() => {
      const context = readWashbasinDomContext(container, item);
      if (context) scheduleComponentViewItem(context);
    }, RETRY_DELAY);
  }

  function readProductAddonContext(detail = {}) {
    const item = detail.item || null;
    const variantId = String(detail.variantId || item?.dataset.addonVariantId || '');

    return {
      source: detail.source || 'product-addons',
      productId: detail.productId || item?.dataset.addonProductId || '',
      variantId,
      title: detail.productTitle || item?.dataset.addonTitle || '',
      vendor: detail.productVendor || item?.dataset.addonVendor || '',
      type: detail.productType || item?.dataset.addonType || '',
      handle: detail.productHandle || item?.dataset.addonHandle || '',
      variantTitle: detail.variantTitle || item?.dataset.addonVariantTitle || '',
      sku: detail.sku || item?.dataset.addonVariantSku || '',
      priceCents: detail.priceCents || item?.dataset.addonPrice || 0,
      compareAtPriceCents: detail.compareAtPriceCents || item?.dataset.addonCompareAtPrice || 0,
      image: normalizeImageUrl(detail.image || item?.dataset.addonImage || ''),
      url: normalizeUrl(detail.url || item?.dataset.addonUrl || window.location.href, variantId),
      currencyCode: getCurrencyCode(detail.currencyCode)
    };
  }

  function updateProductItem(item, data) {
    return {
      brand: data.vendor,
      category: data.type,
      compare_at_price: centsToDecimal(data.compareAtPriceCents),
      id: data.sku || String(data.variantId || data.productId || ''),
      image: normalizeImageUrl(data.image),
      list: item?.list || '',
      name: data.title,
      price: centsToDecimal(data.priceCents),
      product_id: String(data.productId || ''),
      variant: data.variantTitle,
      variant_id: String(data.variantId || '')
    };
  }

  function ensureEcommercePayload(eventPayload, data) {
    eventPayload.ecommerce = eventPayload.ecommerce || {};
    eventPayload.ecommerce.currencyCode = data.currencyCode || eventPayload.ecommerce.currencyCode;
    delete eventPayload.ecommerce.currency;
    delete eventPayload.ecommerce.value;
    delete eventPayload.ecommerce.items;
    delete eventPayload.ecommerce.products;

    const ecommerce = eventPayload.ecommerce;
    ecommerce.detail = ecommerce.detail || {};
    delete ecommerce.detail.items;

    const templateItem = Array.isArray(ecommerce.detail.products) ? ecommerce.detail.products[0] : null;
    ecommerce.detail.products = [updateProductItem(templateItem, data)];
  }

  function updatePagePayload(eventPayload, data) {
    const pageUrl = normalizeUrl(data.url || window.location.href, data.variantId || '');

    eventPayload.page = eventPayload.page && typeof eventPayload.page === 'object'
      ? eventPayload.page
      : {};
    eventPayload.page.url = pageUrl;
    eventPayload.page.path = window.location.pathname;
    eventPayload.page.title = document.title;

    if (Object.prototype.hasOwnProperty.call(eventPayload, 'page_location')) {
      eventPayload.page_location = pageUrl;
    }
    if (Object.prototype.hasOwnProperty.call(eventPayload, 'page_path')) {
      eventPayload.page_path = window.location.pathname;
    }
    if (Object.prototype.hasOwnProperty.call(eventPayload, 'page_title')) {
      eventPayload.page_title = document.title;
    }
  }

  function buildViewItemPayload(data, sourceEvent) {
    const baseEvent = clone(sourceEvent);
    if (!baseEvent) return null;

    baseEvent.event = VIEW_ITEM_EVENT;
    baseEvent.event_id = `bl_${data.source || 'pdp'}_${data.productId || 'product'}_${data.variantId || 'variant'}_${Date.now()}`;
    baseEvent._blSyntheticViewItem = true;
    delete baseEvent.product_id;
    delete baseEvent.variant_id;

    ensureEcommercePayload(baseEvent, data);
    updatePagePayload(baseEvent, data);

    return baseEvent;
  }

  function buildUserDataPayload(data, sourceEvent) {
    const baseEvent = clone(sourceEvent);
    if (!baseEvent) return null;

    baseEvent.event = USER_DATA_EVENT;
    baseEvent.event_id = `bl_user_data_${data.source || 'pdp'}_${data.variantId || 'variant'}_${Date.now()}`;
    baseEvent._blSyntheticUserData = true;

    updatePagePayload(baseEvent, data);

    return baseEvent;
  }

  function eventMatchesData(eventPayload, data) {
    if (!eventPayload || !data) return false;

    const eventIds = getEventItemIds(eventPayload);
    const productMatches = !data.productId || eventIds.productId === normalizeId(data.productId);
    const variantMatches = !data.variantId || eventIds.variantId === normalizeId(data.variantId);

    return productMatches && variantMatches;
  }

  function findMatchingViewItemSince(data, minIndex) {
    const layer = getElevarLayer();
    if (!layer) return null;

    for (let index = layer.length - 1; index >= minIndex; index -= 1) {
      const eventPayload = layer[index];
      if (eventPayload?.event === VIEW_ITEM_EVENT && eventMatchesData(eventPayload, data)) {
        return eventPayload;
      }
    }

    return null;
  }

  function findUserDataSince(minIndex) {
    return getLatestUserDataEvent(minIndex);
  }

  function publishViewItem(data, attempt = 0) {
    if (!data?.productId || !data?.variantId) return;

    const layer = getElevarLayer();
    if (!layer) {
      if (attempt < MAX_RETRIES) {
        window.setTimeout(() => publishViewItem(data, attempt + 1), RETRY_DELAY);
      }
      return;
    }

    const eventKey = `${data.source || 'pdp'}:${data.productId}:${data.variantId}`;
    const now = Date.now();
    if (eventKey === lastPublishedViewItemKey && now - lastPublishedViewItemAt < 600) return;

    const latestEvent = getLatestViewItemEvent();
    if (!latestEvent) {
      if (attempt < MAX_RETRIES) {
        window.setTimeout(() => publishViewItem(data, attempt + 1), RETRY_DELAY);
      }
      return;
    }

    if (latestEvent && eventMatchesData(latestEvent, data) && latestEvent._blSyntheticViewItem !== true) {
      return;
    }

    const payload = buildViewItemPayload(data, latestEvent);
    if (!payload) return;

    logVerbose('push synthetic dl_view_item', {
      source: data.source,
      product_id: data.productId,
      variant_id: data.variantId,
      payload
    });

    layer.push(payload);
    lastPublishedViewItemKey = eventKey;
    lastPublishedViewItemAt = now;

    document.dispatchEvent(new CustomEvent('bl:elevar:view-item', {
      detail: {
        source: data.source,
        productId: data.productId,
        variantId: data.variantId,
        payload
      }
    }));
  }

  function publishUserData(data, attempt = 0) {
    if (!data?.variantId) return;

    const layer = getElevarLayer();
    if (!layer) {
      if (attempt < MAX_RETRIES) {
        window.setTimeout(() => publishUserData(data, attempt + 1), RETRY_DELAY);
      }
      return;
    }

    const eventKey = `${data.source || 'pdp'}:${data.url || window.location.href}:${data.variantId}`;
    const now = Date.now();
    if (eventKey === lastPublishedUserDataKey && now - lastPublishedUserDataAt < 600) return;

    const latestEvent = getLatestUserDataEvent();
    if (!latestEvent) {
      if (attempt < MAX_RETRIES) {
        window.setTimeout(() => publishUserData(data, attempt + 1), RETRY_DELAY);
      }
      return;
    }

    const payload = buildUserDataPayload(data, latestEvent);
    if (!payload) return;

    logVerbose('push synthetic dl_user_data', {
      source: data.source,
      variant_id: data.variantId,
      payload
    });

    layer.push(payload);
    lastPublishedUserDataKey = eventKey;
    lastPublishedUserDataAt = now;

    document.dispatchEvent(new CustomEvent('bl:elevar:user-data', {
      detail: {
        source: data.source,
        variantId: data.variantId,
        payload
      }
    }));
  }

  function scheduleViewItem(data, options = {}) {
    if (!data?.productId || !data?.variantId) return;

    const delay = options.delay ?? RETRY_DELAY;
    const startIndex = options.startIndex ?? getLayerLength();

    window.setTimeout(() => {
      if (findMatchingViewItemSince(data, startIndex)) {
        return;
      }

      publishViewItem(data);
    }, delay);
  }

  function scheduleUserData(data, options = {}) {
    if (!data?.variantId) return;

    const delay = options.delay ?? RETRY_DELAY;
    const startIndex = options.startIndex ?? getLayerLength();

    window.setTimeout(() => {
      if (findUserDataSince(startIndex)) {
        return;
      }

      publishUserData(data);
    }, delay);
  }

  function schedulePdpNavigationEvents(data, options = {}) {
    if (!data?.productId || !data?.variantId) return;

    const startIndex = options.startIndex ?? getLayerLength();
    const delay = options.delay ?? NATIVE_NAVIGATION_DELAY;

    scheduleViewItem(data, { delay, startIndex });
    scheduleUserData(data, { delay, startIndex });
  }

  function scheduleComponentViewItem(data) {
    if (!COMPONENT_VIEW_ITEM_ENABLED) {
      logVerbose('skip component dl_view_item — pending Elevar confirmation', {
        source: data?.source,
        product_id: data?.productId,
        variant_id: data?.variantId
      });
      return;
    }

    scheduleViewItem(data);
  }

  function cancelPendingVariantNavigation() {
    if (!pendingVariantNavigationTimer) return;
    window.clearTimeout(pendingVariantNavigationTimer);
    pendingVariantNavigationTimer = null;
  }

  document.addEventListener('bl:grouping:swapped', () => {
    const startIndex = getNavigationStartIndex();
    cancelPendingVariantNavigation();
    window.requestAnimationFrame(() => {
      schedulePdpNavigationEvents(readMainProductContext('grouping'), {
        startIndex,
        delay: NATIVE_NAVIGATION_DELAY
      });
    });
  });

  document.addEventListener('variant:changed', (event) => {
    const variantId = normalizeId(event.detail?.variant?.id);
    if (!variantId) return;

    const startIndex = getNavigationStartIndex();
    cancelPendingVariantNavigation();
    pendingVariantNavigationTimer = window.setTimeout(() => {
      pendingVariantNavigationTimer = null;
      const data = readMainProductContext('variant');
      if (!data.variantId) data.variantId = variantId;
      schedulePdpNavigationEvents(data, {
        startIndex,
        delay: NATIVE_NAVIGATION_DELAY
      });
    }, VARIANT_NAVIGATION_DEBOUNCE);
  });

  document.addEventListener('bl:washbasin-v2:view-item', (event) => {
    scheduleComponentViewItem(readWashbasinContext(event.detail || {}));
  });

  document.addEventListener('click', (event) => {
    const container = closestElement(event.target, '[data-washbasin-selector-v2]');
    if (!container) return;

    const pill = closestElement(event.target, '[data-pill-value]');
    const clickedItem = closestElement(event.target, '.washbasin-selector__item[data-washbasin-id]');
    const basinId = pill?.dataset.pillValue || clickedItem?.dataset.washbasinId || '';
    if (!basinId || basinId === 'none') return;

    const item = clickedItem || findWashbasinItem(container, basinId);
    if (!item) return;

    scheduleWashbasinDomViewItem(container, item);
  });

  document.addEventListener('change', (event) => {
    const input = closestElement(event.target, '.washbasin-selector__input');
    const container = input?.closest('[data-washbasin-selector-v2]');
    if (!container || !input?.checked || !input.value || input.value === 'none') return;

    const item = input.closest('.washbasin-selector__item[data-washbasin-id]') || findWashbasinItem(container, input.value);
    if (!item) return;

    scheduleWashbasinDomViewItem(container, item);
  });

  document.addEventListener('bl:product-addons:view-item', (event) => {
    scheduleComponentViewItem(readProductAddonContext(event.detail || {}));
  });

  installHistoryBoundaryTracker();
})();
